import { useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import type { Id } from '../../convex/_generated/dataModel';
import { useConvexMutation } from './use-convex-mutation';

// ════════════════════════════════════════════════════════════
// TYPES
// ════════════════════════════════════════════════════════════

interface EvaluationData {
  playerId: string;
  sessionId?: string;
  attitude: number;
  effort: number;
  engagement: number;
  physical: number;
  technical: number;
  tactical: number;
  notes?: string;
}

export interface EvaluationCriterion {
  _id: string;
  name: string;
  category: string;
}

// ════════════════════════════════════════════════════════════
// MUTATION HOOKS
// ════════════════════════════════════════════════════════════

export function useSessionEvaluations(sessionId: string) {
  const data = useQuery(
    api.coach.evaluations.getForSession,
    sessionId ? { sessionId: sessionId as Id<"trainingSessions"> } : 'skip'
  );
  return { data, isLoading: data === undefined };
}

export function usePlayerCriteria(playerId: string | undefined) {
  const data = useQuery(
    api.coach.evaluations.getCriteriaForPlayer,
    playerId ? { playerId: playerId as Id<"players"> } : 'skip'
  );
  return { data, isLoading: data === undefined };
}

export function useDeleteEvaluation() {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.evaluations.remove);
  return {
    isPending,
    mutateAsync: (evaluationId: string) =>
      mutateAsync({ evaluationId: evaluationId as Id<"evaluations"> }),
  };
}

export function useSubmitEvaluation() {
  const { mutateAsync, isPending } = useConvexMutation(api.coach.evaluations.submit);
  return {
    isPending,
    mutateAsync: (data: EvaluationData) => mutateAsync({
      playerId: data.playerId as Id<"players">,
      sessionId: data.sessionId ? data.sessionId as Id<"trainingSessions"> : undefined,
      attitude: data.attitude,
      effort: data.effort,
      engagement: data.engagement,
      physical: data.physical,
      technical: data.technical,
      tactical: data.tactical,
      notes: data.notes,
    }),
  };
}

